import { useNavigate, useOutletContext } from 'react-router-dom';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import UsageCell from '../../../components/AiSpend/UsageCell';
import Pagination from '../../../components/Table/Pagination';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import { getAiSpendUsers } from '../../../services/aiSpendService';
import {
  formatCount,
  formatInstant,
  formatMoney,
} from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';
import { AI_SPEND_BASE } from './aiSpendPages';

export default function AiSpendUsers() {
  const navigate = useNavigate();
  const { filters, setFilters, queryParams, queryKey, options } = useOutletContext();
  const ordering = filters.ordering || '-cost_usd';

  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) =>
      getAiSpendUsers({
        params: {
          ...queryParams,
          ordering,
          page: filters.page,
          page_size: filters.page_size,
        },
        signal,
      }),
    { deps: [queryKey] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const rows = data?.results || [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          Spend by student
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The student the spend is <em>about</em>. Who triggered a call is a separate
          dimension — an expert regenerating an evaluation spends on a student&apos;s
          behalf.
        </p>
      </div>
      <AnalyticsTable
        loading={loading}
        rows={rows}
        rowKey={(row) => row.subject}
        sort={ordering}
        onSortChange={(next) => setFilters({ ordering: next })}
        onRowClick={(row) =>
          navigate({
            pathname: `${AI_SPEND_BASE}/users/${row.subject}`,
            search: window.location.search,
          })
        }
        emptyTitle="No attributed spend"
        emptyMessage="No calls in this period could be attributed to a student."
        columns={[
          { key: 'email', label: 'Student', render: (row) => row.email },
          {
            key: 'cost_usd',
            label: 'Cost',
            sortKey: 'cost_usd',
            render: (row) => formatMoney(row.cost_usd),
            className: 'font-semibold',
          },
          {
            key: 'calls',
            label: 'Calls',
            sortKey: 'calls',
            render: (row) => formatCount(row.calls),
          },
          {
            key: 'failed_calls',
            label: 'Failed',
            render: (row) => formatCount(row.failed_calls),
          },
          {
            key: 'usage',
            label: 'Usage',
            render: (row) => <UsageCell usage={row.usage} billingUnit={row.billing_unit} />,
          },
          {
            key: 'last_seen',
            label: 'Last activity',
            sortKey: 'last_seen',
            render: (row) => formatInstant(row.last_seen, options?.timezone),
          },
        ]}
        footer={
          <Pagination
            page={filters.page}
            pageSize={filters.page_size}
            count={data?.count || 0}
            itemLabel="students"
            onPageChange={(page) => setFilters({ page })}
          />
        }
      />
    </div>
  );
}
